import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Visual-only digit motion for `MfaOtpInputs` (2026-09-25).
 *
 * Diffs the code string between renders and reports which cells should play
 * the enter beat (glyph fades + scales in) and which should show an exit
 * ghost (the removed glyph fades + shrinks out over an already-empty cell).
 * The `value` itself is never touched — the parent owns it and it is set
 * synchronously; this only drives classes that a plain timer removes (never
 * `animationend`, which iOS PWA drops).
 *
 * - One typed digit → that cell enters, no delay.
 * - Two or more digits landing at once (paste / one-time-code autofill) →
 *   a left-to-right cascade, `OTP_CASCADE_STEP_MS` apart (CSS delay only).
 * - A bulk clear (more than one cell emptied at once — error flush, reset)
 *   gets no ghosts: status chrome wins.
 * - `enabled` false (reduced motion, or an error/verified status) → nothing.
 */
export const OTP_ENTER_MS = 140;
export const OTP_EXIT_MS = 110;
export const OTP_CASCADE_STEP_MS = 30;

const IDLE = { gen: 0, enter: {}, exit: {} };
// Slack after the last beat so the class never drops mid-frame.
const SETTLE_MS = 30;

export function diffOtpDigits(prev, next) {
  const enter = {};
  const exit = {};
  const cascade = next.length - prev.length >= 2;
  let order = 0;
  for (let i = 0; i < 6; i += 1) {
    const a = prev[i] || '';
    const b = next[i] || '';
    if (b && b !== a) {
      enter[i] = cascade ? order * OTP_CASCADE_STEP_MS : 0;
      order += 1;
    } else if (a && !b) {
      exit[i] = a;
    }
  }
  if (Object.keys(exit).length > 1) return { enter, exit: {} };
  return { enter, exit };
}

export default function useOtpDigitMotion(value, enabled) {
  const prevRef = useRef(value);
  const timerRef = useRef(null);
  const [motion, setMotion] = useState(IDLE);

  // Layout effect: the class lands in the same paint as the new digit, so the
  // glyph never flashes at full size first.
  useLayoutEffect(() => {
    const prev = prevRef.current;
    prevRef.current = value;
    if (!enabled || prev === value) return;
    const { enter, exit } = diffOtpDigits(prev, value);
    const delays = Object.values(enter);
    const hasExit = Object.keys(exit).length > 0;
    if (!delays.length && !hasExit) return;
    const life = Math.max(
      delays.length ? OTP_ENTER_MS + Math.max(...delays) : 0,
      hasExit ? OTP_EXIT_MS : 0,
    ) + SETTLE_MS;
    setMotion((m) => ({ gen: m.gen + 1, enter, exit }));
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setMotion((m) => ({ gen: m.gen, enter: {}, exit: {} }));
    }, life);
  }, [value, enabled]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return enabled ? motion : IDLE;
}
